// src/components/Header/NavigationItem.tsx
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import React, { FC, Fragment, useState } from "react";
import { NavLink } from "react-router-dom";
import { LocationStates } from "routers/types";
import {
  Printer,
  Bot,
  Wrench,
  Sparkles,
  ArrowRight,
  BookOpen,
  GraduationCap,
  Users,
  FileText,
  MessageSquare,
  Store,
  Quote,
  Info,
  Mail,
} from "lucide-react";
import megaMenuImg3dPrinting from "images/collections/3d-printer-set-Photoroom.png";
import megaMenuImgRobotics from "images/collections/robotics-myarm-m750.png";
import megaMenuImgAccessories from "images/collections/service-parts-pc-Photoroom.png";

export interface NavItemType {
  id: string;
  name: string;
  href: keyof LocationStates | string;
  target?: "_blank" | "_self" | "_parent" | "_top";
  children?: NavItemType[];
  type?: "dropdown" | "megaMenu" | "none";
  isNew?: boolean;
}

export interface NavigationItemProps {
  menuItem: NavItemType;
}

// ─── Self-contained featured card ────────────────────────────────────────────
interface FeaturedCardProps {
  image: string;
  tagline: string;
  name: string;
  subtext: string;
  link: string;
  onClick: () => void;
}

const MegaMenuFeaturedCard: FC<FeaturedCardProps> = ({
  image,
  tagline,
  name,
  subtext,
  link,
  onClick,
}) => (
  <NavLink
    to={link}
    onClick={onClick}
    className="group block cursor-pointer rounded-xl overflow-hidden border border-slate-100 dark:border-slate-700/60 hover:border-orange-200 dark:hover:border-orange-500/30 bg-white dark:bg-neutral-800/80 shadow-sm hover:shadow-md transition-all duration-200"
  >
    {/* Image panel */}
    <div className="relative w-full h-[152px] bg-gradient-to-br from-slate-50 via-orange-50/40 to-orange-100/30 dark:from-neutral-800 dark:via-neutral-800 dark:to-orange-900/10 flex items-center justify-center overflow-hidden">
      <img
        src={image}
        alt={name}
        className="h-[120px] w-auto object-contain transition-transform duration-300 group-hover:scale-[1.04] drop-shadow-md"
      />
      {/* Bottom fade so image bleeds softly into text */}
      <div className="absolute bottom-0 inset-x-0 h-10 bg-gradient-to-t from-white dark:from-neutral-800/80 to-transparent" />
    </div>

    {/* Text panel */}
    <div className="px-4 pt-3 pb-4 space-y-1">
      <p className="text-[10px] font-bold text-[#ff6600] tracking-wider">
        {tagline.toUpperCase()}
      </p>
      <p className="text-[13px] font-bold text-slate-900 dark:text-neutral-100 leading-snug">
        {name}
      </p>
      <p className="text-[11px] text-slate-400 dark:text-neutral-500 leading-relaxed line-clamp-2">
        {subtext}
      </p>
      <div className="flex items-center gap-1 pt-2 text-[#ff6600] text-[11px] font-bold">
        Shop now
        <ArrowRight className="w-3 h-3 transition-transform duration-150 group-hover:translate-x-0.5" />
      </div>
    </div>
  </NavLink>
);
// ─────────────────────────────────────────────────────────────────────────────

const NavigationItem: FC<NavigationItemProps> = ({ menuItem }) => {
  const [menuCurrentHovers, setMenuCurrentHovers] = useState<string[]>([]);

  const onMouseEnterMenu = (id: string) =>
    setMenuCurrentHovers((s) => [...s, id]);

  const onMouseLeaveMenu = (id: string) =>
    setMenuCurrentHovers((s) => s.filter((i) => i !== id));

  const handleClick = () => setMenuCurrentHovers([]);

  const getIconForName = (name: string) => {
    const n = name.toLowerCase();
    if (n.includes("printers")) return Printer;
    if (n.includes("robotics") || n.includes("arms") || n.includes("pets") || n.includes("humanoids")) return Bot;
    if (n.includes("parts") || n.includes("accessories") || n.includes("gear")) return Wrench;
    if (n.includes("buyers guide")) return BookOpen;
    if (n.includes("stem") || n.includes("academy")) return GraduationCap;
    if (n.includes("research")) return Users;
    if (n.includes("manuals") || n.includes("docs")) return FileText;
    if (n.includes("forum") || n.includes("community")) return MessageSquare;
    if (n.includes("makerstore")) return Store;
    if (n.includes("quotes")) return Quote;
    if (n.includes("about")) return Info;
    if (n.includes("contact")) return Mail;
    return Sparkles;
  };

  const getFeaturedData = (name: string): FeaturedCardProps => {
    const n = name.toLowerCase();
    if (n.includes("robotics"))
      return {
        image: megaMenuImgRobotics,
        tagline: "Professional Robotics",
        name: "Precision Cobots For Everyone",
        subtext: "Programmable arms built for R&D, education, and light industry.",
        link: "/robotics",
        onClick: handleClick,
      };
    if (n.includes("3d printing"))
      return {
        image: megaMenuImg3dPrinting,
        tagline: "3D Printing Ecosystem",
        name: "Next-Gen Precision Meets Innovation",
        subtext: "Professional-grade machines designed for serious creators.",
        link: "/printers",
        onClick: handleClick,
      };
    if (n.includes("accessories"))
      return {
        image: megaMenuImgAccessories,
        tagline: "Essential Gear",
        name: "Tools & Enhancements",
        subtext: "Everything you need to optimise your workflow.",
        link: "/accessories",
        onClick: handleClick,
      };
    return {
      image: megaMenuImg3dPrinting,
      tagline: "Featured Collection",
      name: "Premium Selection",
      subtext: "Discover our top-rated products, hand-picked for you.",
      link: "/page-search",
      onClick: handleClick,
    };
  };

  // ─── Mega menu ──────────────────────────────────────────────────────────────
  const renderMegaMenu = (menu: NavItemType) => {
    if (!menu.children) return null;
    const isHover = menuCurrentHovers.includes(menu.id);
    const featuredData = getFeaturedData(menu.name);

    return (
      <Popover
        as="li"
        className="flex-shrink-0 h-full flex"
        onMouseEnter={() => onMouseEnterMenu(menu.id)}
        onMouseLeave={() => onMouseLeaveMenu(menu.id)}
      >
        <>
          <Popover.Button as={Fragment}>{renderMainItem(menu)}</Popover.Button>
          <Transition
            as={Fragment}
            show={isHover}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-2"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel static className="sub-menu absolute top-full inset-x-0 transform z-50 w-full">
              <style dangerouslySetInnerHTML={{ __html: `
                .mega-scrollbar::-webkit-scrollbar { width: 4px; }
                .mega-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .mega-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 99px; }
                .dark .mega-scrollbar::-webkit-scrollbar-thumb { background: #334155; }
                .mega-nav-link::before {
                  content: ''; display: block; width: 0; height: 1.5px;
                  background: #ff6600; transition: width 0.18s ease; margin-top: 1px;
                }
                .mega-nav-link:hover::before { width: 10px; }
                @keyframes mega-fade-up {
                  from { opacity: 0; transform: translateY(6px); }
                  to   { opacity: 1; transform: translateY(0); }
                }
                .mega-col { animation: mega-fade-up 0.22s ease both; }
              `}} />

              {/* Orange anchor line */}
              <div className="h-[2px] bg-gradient-to-r from-[#ff6600]/80 via-[#ff6600]/40 to-transparent" />

              <div className="bg-white dark:bg-neutral-900 shadow-lg border-b border-slate-100 dark:border-slate-800 max-h-[72vh] overflow-y-auto mega-scrollbar">
                <div className="container mx-auto">
                  <div className="flex flex-col lg:flex-row items-stretch">

                    {/* Navigation columns */}
                    <div className="flex-1 py-7 lg:py-9 pr-0 lg:pr-10">
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-8 gap-y-8">
                        {menu.children.map((item, index) => {
                          const Icon = getIconForName(item.name);
                          return (
                            <div
                              key={index}
                              className="mega-col space-y-3"
                              style={{ animationDelay: `${index * 35}ms` }}
                            >
                              <div className="flex items-center gap-2 pb-2.5 border-b border-slate-100 dark:border-slate-800">
                                <div className="flex items-center justify-center w-6 h-6 rounded-md bg-orange-50 dark:bg-orange-500/10 flex-shrink-0">
                                  <Icon className="w-3.5 h-3.5 text-[#ff6600]" />
                                </div>
                                <p className="text-sm font-bold text-slate-900 dark:text-neutral-100">
                                  {item.name}
                                </p>
                              </div>
                              <ul className="space-y-0.5">
                                {item.children?.map(renderMegaMenuNavlink)}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Featured sidebar */}
                    <div className="hidden lg:flex flex-col w-[260px] xl:w-[290px] relative border-l border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-neutral-800/40">
                      <div className="absolute left-0 top-8 bottom-8 w-[2px] bg-gradient-to-b from-[#ff6600]/60 via-[#ff6600]/20 to-transparent rounded-r" />
                      <div className="p-6 flex flex-col gap-4 h-full">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-bold text-slate-900 dark:text-neutral-100">
                            Featured
                          </h3>
                          <NavLink
                            to="/page-search"
                            onClick={handleClick}
                            className="flex cursor-pointer items-center gap-1 text-[#ff6600] text-xs font-semibold hover:gap-1.5 transition-all duration-150"
                          >
                            View All
                            <ArrowRight className="w-3 h-3" />
                          </NavLink>
                        </div>
                        <MegaMenuFeaturedCard {...featuredData} />
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      </Popover>
    );
  };

  const renderMegaMenuNavlink = (item: NavItemType) => {
    const isViewAll = item.name.toLowerCase().includes("view all");
    return (
      <li key={item.id}>
        <NavLink
          to={item.href as keyof LocationStates}
          onClick={handleClick}
          className={`mega-nav-link flex cursor-pointer flex-col transition-colors duration-150 py-1 text-sm ${
            isViewAll
              ? "font-bold text-slate-800 dark:text-neutral-200 mt-1.5"
              : "font-medium text-slate-500 dark:text-neutral-400 hover:text-[#ff6600] dark:hover:text-[#ff6600]"
          }`}
        >
          {item.name}
        </NavLink>
      </li>
    );
  };

  // ─── Dropdown menu ──────────────────────────────────────────────────────────
  const renderDropdownMenu = (menuDropdown: NavItemType) => {
    const isHover = menuCurrentHovers.includes(menuDropdown.id);
    return (
      <Popover
        as="li"
        className="menu-item menu-dropdown relative flex-shrink-0 h-full flex"
        onMouseEnter={() => onMouseEnterMenu(menuDropdown.id)}
        onMouseLeave={() => onMouseLeaveMenu(menuDropdown.id)}
      >
        <>
          <Popover.Button as={Fragment}>{renderMainItem(menuDropdown)}</Popover.Button>
          <Transition
            as={Fragment}
            show={isHover}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-2 scale-95"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-1 scale-95"
          >
            <Popover.Panel static className="sub-menu absolute z-10 w-64 top-full left-0 pt-2">
              <div className="rounded-xl shadow-lg ring-1 ring-black/5 dark:ring-white/10 bg-white dark:bg-neutral-900 overflow-hidden border border-slate-100 dark:border-slate-800">
                <div className="h-[2px] bg-gradient-to-r from-[#ff6600]/80 via-[#ff6600]/40 to-transparent" />
                <ul className="py-2 px-1.5 space-y-0.5">
                  {menuDropdown.children?.map(renderDropdownMenuNavlink)}
                </ul>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      </Popover>
    );
  };

  const renderDropdownMenuNavlink = (item: NavItemType) => {
    const Icon = getIconForName(item.name);
    return (
      <li key={item.id}>
        <NavLink
          to={item.href as keyof LocationStates}
          target={item.target}
          onClick={handleClick}
          className={({ isActive }) =>
            `flex cursor-pointer items-center px-3 py-2.5 rounded-lg transition-all duration-150 group ${
              isActive
                ? "bg-orange-50 dark:bg-orange-500/10 text-[#ff6600]"
                : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 hover:text-[#ff6600] dark:hover:text-[#ff6600]"
            }`
          }
        >
          <div className="flex items-center justify-center w-7 h-7 mr-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-100 dark:border-slate-700 group-hover:border-orange-200 dark:group-hover:border-orange-500/30 group-hover:bg-orange-50 dark:group-hover:bg-orange-500/10 transition-all duration-150 flex-shrink-0">
            <Icon className="w-3.5 h-3.5" />
          </div>
          <span className="font-semibold text-sm">{item.name}</span>
          {item.target === "_blank" && (
            <svg className="ml-auto w-3 h-3 opacity-30 group-hover:opacity-70 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          )}
        </NavLink>
      </li>
    );
  };

  // ─── Shared renderers ───────────────────────────────────────────────────────
  const renderMainItem = (item: NavItemType) => {
    const content = renderNavLink(item);

    if (item.type === "dropdown" || item.type === "megaMenu") {
      return content;
    }

    return <div className="flex-shrink-0 flex items-center h-full">{content}</div>;
  };

  const renderNavLink = (item: NavItemType, location: "main" | "dropdown" = "main") => {
    const isExternal = typeof item.href === "string" && item.href.startsWith("http");
    const isDropdown = location === "dropdown";
    const isMainItemWithMenu = location === "main" && (item.type === "dropdown" || item.type === "megaMenu");

    const commonClasses = `cursor-pointer flex items-center font-medium transition-all duration-200 whitespace-nowrap ${
      isDropdown
        ? "py-3 px-4 rounded-xl text-slate-700 dark:text-slate-300"
        : "h-full px-4 xl:px-5 text-slate-800 dark:text-slate-200 hover:text-[#ff6600] dark:hover:text-[#ff6600] text-sm lg:text-[15px] relative"
    }`;

    if (isMainItemWithMenu) {
      return (
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={menuCurrentHovers.includes(item.id)}
          className={`${commonClasses} group`}
        >
          {item.name}
          <ChevronDownIcon
            className="ml-1.5 -mr-1 h-3.5 w-3.5 text-slate-300 dark:text-slate-600 transition-colors duration-200 group-hover:text-[#ff6600]"
            aria-hidden="true"
          />
        </button>
      );
    }

    return isExternal ? (
      <a
        href={item.href}
        target={item.target ?? "_self"}
        onClick={handleClick}
        rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
        className={commonClasses}
      >
        {item.name}
        {item.type && (
          <ChevronDownIcon className="ml-1 -mr-1 h-3.5 w-3.5 text-slate-300 group-hover:text-[#ff6600] transition-colors" aria-hidden="true" />
        )}
      </a>
    ) : (
      <NavLink
        to={item.href as keyof LocationStates}
        target={item.target}
        onClick={handleClick}
        rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
        className={({ isActive }) =>
          `${commonClasses} ${isActive ? "text-[#ff6600] dark:text-[#ff6600]" : ""}`
        }
      >
        {({ isActive }) => (
          <>
            {item.name}
            {item.type && (
              <ChevronDownIcon
                className={`${isDropdown ? "ml-auto pl-2" : "ml-1.5 -mr-1"} h-3.5 w-3.5 text-slate-300 dark:text-slate-600 transition-colors duration-200 group-hover:text-[#ff6600]`}
                aria-hidden="true"
              />
            )}
            {isActive && !isDropdown && (
              <span className="absolute -bottom-px left-4 right-4 h-[2px] bg-[#ff6600] rounded-full" />
            )}
          </>
        )}
      </NavLink>
    );
  };

  switch (menuItem.type) {
    case "dropdown":
      return renderDropdownMenu(menuItem);
    case "megaMenu":
      return renderMegaMenu(menuItem);
    default:
      return <li className="menu-item flex-shrink-0">{renderMainItem(menuItem)}</li>;
  }
};

export default NavigationItem;